// Boston College: public college page (#2746), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
// #3859 (2026-10-11), page vs KB: the headline class is now Fall 2025 (Class
// of 2029, BC's admission profile via the Internet Archive), the class the
// KB holds; residency stays the Fall 2024 Common Data Set's. BC's live
// profile now shows the Class of 2030 (41,923 applied, 5,636 admitted).
//  - Boston College's Common Data Set 2024-2025 (Institutional Research and
//    Planning, Boston_College_CDS_2024-2025_Final.pdf), the newest edition
//    BC has posted: C1 applicants, admits and enrollees for Fall 2024 with
//    the residency table, C21 early decision counts for the Fall 2024 class.
//    Departure: no 2025-2026 CDS is posted yet, so the counts are a year
//    older than most pages here, and labelled Fall 2024.
//  - BC Undergraduate Admission: Compare Early Decision and Regular Decision
//    (the plan table and binding rules), Apply (application fee, testing,
//    arts portfolios) and Test-Optional Policy. The pages name no entry year;
//    they are the pages as posted for the application open now (Fall 2027).
//  - BC Student Services, Undergraduate Financial Aid: How Aid Works, the
//    2026-27 cost of attendance for an entering student living on campus.
//  - College Scorecard, school id 164924 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Boston College Common Data Set 2024-2025',
        url: 'https://www.bc.edu/content/dam/bc1/offices/irp/ir/cds/Boston_College_CDS_2024-2025_Final.pdf',
        read: '2026-10-01',
    },
    plans: {
        label: 'Boston College Admission, Compare Early Decision and Regular Decision',
        url: 'https://www.bc.edu/bc-web/admission/apply/early-decision.html',
        read: '2026-10-01',
    },
    apply: {
        label: 'Boston College Admission, Apply',
        url: 'https://www.bc.edu/bc-web/admission/apply.html',
        read: '2026-10-01',
    },
    testing: {
        label: 'Boston College Admission, Test-Optional Policy',
        url: 'https://www.bc.edu/bc-web/admission/apply/test-optional.html',
        read: '2026-10-01',
    },
    costs: {
        label: 'Boston College Undergraduate Financial Aid, How Aid Works',
        url: 'https://www.bc.edu/bc-web/offices/student-services/financial-aid/undergraduate/how-aid-works.html',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?164924-Boston-College',
        read: '2026-10-15',
    },
    admissionProfile: {
        label: 'Boston College First-Year Admission Profile, Class of 2029',
        url: 'https://web.archive.org/web/20260113040655/https://www.bc.edu/content/bc-web/admission/apply/admission-statistics.html',
        read: '2026-10-11',
    },
};

const college = {
    slug: 'boston-college',
    kbId: 'boston_college',
    name: 'Boston College',
    shortName: 'BC',
    location: 'Chestnut Hill, MA',
    control: 'Private',
    lastChecked: '2026-10-11',
    sources: SOURCES,
    // From the KB's sourced branding (Boston College colors); decoration only.
    branding: {
        primary: '#8A100B',
        secondary: '#B29D6C',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/boston_college_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: null,
        applied: 39686,
        admitted: 5497,
        enrolled: 2479,
        source: 'admissionProfile',
        where: 'First-Year Admission Profile, Class of 2029 (2025 annual census data). BC\'s newest Common Data Set (2024-2025) reports the Fall 2024 class, so the residency table below is that class',
        residency: [
            { group: 'In-state', applied: 5401, admitted: 896 },
            { group: 'Out-of-state', applied: 25661, admitted: 4116 },
            { group: 'International', applied: 3696, admitted: 618 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table (21 applicants of unknown residency, 2 admitted, are not shown)',
        residencyEntering: 'Fall 2024',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'BC\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major. BC has not yet posted a 2025-2026 Common Data Set, so these figures are for the class that entered in fall 2024.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision',
        note: 'Counts for Early Decision I and II together. BC has no early action plan.',
        year: 'Fall 2024',
        rows: [
            { plan: 'Early Decision (I and II)', applied: 4288, admitted: 1434 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to one of BC\'s four undergraduate schools: the Morrissey College of Arts and Sciences, the Carroll School of Management, the Lynch School of Education and Human Development, or the Connell School of Nursing. A few programs work differently:',
        rows: [
            { school: 'Nursing', requirement: 'Apply directly to the Connell School of Nursing. Nursing and Human-Centered Engineering first-years cannot start in the spring.', source: 'apply' },
            { school: 'Studio Art, theater and music', requirement: 'Optional: Studio Art majors can send a portfolio through SlideRoom, and so can students with a theater or music background, whatever their major. SlideRoom materials are due November 1 for Early Decision I and January 4 for Early Decision II and Regular Decision.', source: 'apply' },
        ],
        source: 'apply',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 4' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'By December 15', binding: true },
            { plan: 'Early Decision II', deadline: 'January 4', decision: 'By February 15', binding: true },
            { plan: 'Regular Decision', deadline: 'January 4', decision: 'By April 1', binding: false },
        ],
        note: 'Regular Decision applicants reply by May 1. If you applied Regular Decision and BC becomes your first choice, you can switch to Early Decision II by emailing the signed Early Decision agreement by January 8. The application fee is $85, and U.S. citizens and permanent residents can ask for a waiver through the Common App.',
        source: 'plans',
        binding: 'Both Early Decision plans are binding: if admitted, you withdraw your other applications, enroll at BC and pay the enrollment confirmation fee within 10 days. While applying Early Decision you may not apply to another binding early decision plan, but you may apply to non-restrictive early action plans elsewhere.',
        bindingSource: 'plans',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'BC is test-optional: if you do not send scores you get full consideration. If you send SAT or ACT scores, BC superscores them, and you can self-report them; enrolled students then send official reports.',
        source: 'testing',
        cdsLine: 'The 2024-2025 Common Data Set also marks the SAT or ACT as considered if submitted, not required, for the fall 2026 applicants it describes.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'testing',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 75070 },
            { item: 'Fees (first year)', amount: 2074 },
            { item: 'Food and housing (on campus)', amount: 19870 },
            { item: 'Books and supplies', amount: 1300 },
            { item: 'Personal expenses', amount: 2000 },
            { item: 'Loan fees', amount: 51 },
            { item: 'Travel', amount: null },
        ],
        total: 100365,
        note: 'For an entering student living on campus. Travel varies from $150 to $1,800 by where you live, so it is not in the $100,365 total. Massachusetts requires health coverage; BC\'s plan is $4,723 for 2026-27 unless you waive it with comparable coverage.',
        source: 'costs',
        netPrice: {
            average: 38930,
            byIncome: [
                { income: '$0 to $30,000', amount: 7293 },
                { income: '$30,001 to $48,000', amount: 10451 },
                { income: '$48,001 to $75,000', amount: 9615 },
                { income: '$75,001 to $110,000', amount: 23616 },
                { income: 'Over $110,000', amount: 61800 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from BC\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
